import { useMemo, useState } from 'react';
import { motion, fadeInUp, slideInRight, AnimatePresence } from '../lib/motion-utils';
import { useLocation, useNavigate } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { useEnterpriseOrg } from '../context/EnterpriseOrgContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { ArrowLeft, Building2, HeartPulse } from 'lucide-react';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '/utils/supabase/info';
import { calculatePersonalizedRTI } from '../lib/personalization';
import { refreshMicroActions } from '../lib/micro-actions';
import { EmotionalStateGauge } from '../components/EmotionalStateGauge';
import { RTIExplanation } from '../components/RTIExplanations';
import { TriggerSelector } from '../components/TriggerSelector';
import { ContextJournal } from '../components/ContextJournal';
import { InfoHint } from '../components/InfoHint';
import { withErrorBoundary } from '../components/ErrorBoundary';
import { useSubscriptionPlan } from '../lib/use-subscription-plan';
import { persistServerCheckIn } from '../lib/enterprise-api';
import { appendEnterpriseWorkspaceCheckIn } from '../lib/enterprise-workspace-local';
import { EnterpriseWorkspaceNav } from '../components/enterprise/EnterpriseWorkspaceNav';
import { useEnterpriseWorkspaceRole } from '../hooks/use-enterprise-workspace-role';
import { workspacePath } from '../lib/enterprise-work-routes';

type RelationshipDomain = 'leadership' | 'friendship' | 'family' | 'romantic';

function EnterpriseCheckIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const { accessToken } = useAuth();
  const { activeOrgId, hasActiveOrgAccess, activeMembership } = useEnterpriseOrg();
  const { plan: subscription } = useSubscriptionPlan();
  const { role } = useEnterpriseWorkspaceRole();

  const [step, setStep] = useState<'rti' | 'emotional' | 'triggers' | 'journal'>('rti');
  const [domain, setDomain] = useState<RelationshipDomain>('leadership');
  const [entityLabel, setEntityLabel] = useState('');
  const [initiation, setInitiation] = useState([50]);
  const [latency, setLatency] = useState([50]);
  const [emotionalDensity, setEmotionalDensity] = useState([50]);
  const [availability, setAvailability] = useState([50]);
  const [withdrawal, setWithdrawal] = useState([50]);
  const [emotionalStates, setEmotionalStates] = useState<number[]>([]);
  const [triggers, setTriggers] = useState<string[]>([]);
  const [journalNote, setJournalNote] = useState('');
  const [loading, setLoading] = useState(false);

  const orgBadge = useMemo(() => {
    if (!activeOrgId) return 'No org selected';
    return `${activeMembership?.organizations?.name || 'Org'} (${activeOrgId.slice(0, 8)}...)`;
  }, [activeMembership?.organizations?.name, activeOrgId]);
  const roleHint =
    role === 'executive'
      ? 'Capture organization-level dynamics affecting leadership confidence and decision cadence.'
      : role === 'hr'
        ? 'Capture support, safety, and intervention-relevant relationship dynamics.'
        : role === 'manager'
          ? 'Capture team interaction strain, response delay, and repair readiness.'
          : 'Capture cross-functional and partner collaboration friction patterns.';

  const handleEmotionalStateSelect = (value: number) => {
    if (emotionalStates.length < 5) setEmotionalStates([...emotionalStates, value]);
  };

  const handleSubmit = async () => {
    if (!activeOrgId || !hasActiveOrgAccess) {
      toast.error('Active enterprise org is required. Set org context first.');
      return;
    }
    setLoading(true);
    try {
      if (emotionalStates.length === 0) {
        toast.error('Select at least one emotional state.');
        setLoading(false);
        return;
      }

      const components = {
        initiation: initiation[0],
        latency: latency[0],
        emotionalDensity: emotionalDensity[0],
        availability: availability[0],
        withdrawal: withdrawal[0],
      };

      const emotionalAverage = Math.round(emotionalStates.reduce((sum, val) => sum + val, 0) / emotionalStates.length);
      let rtiScore = calculatePersonalizedRTI(components);

      if (accessToken) {
        try {
          const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/rti/compute`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${publicAnonKey}`,
              'X-User-Token': accessToken,
            },
            body: JSON.stringify({ components }),
          });
          if (res.ok) {
            const data = await res.json();
            if (typeof data.score === 'number') rtiScore = data.score;
          }
        } catch {
          // Keep client score fallback.
        }
      }

      const result = await persistServerCheckIn(
        {
          relationshipId: null,
          relationshipDomain: domain,
          rtiScore,
          emotionalAverage,
          components,
          triggers,
          metadata: {
            source: 'enterprise-checkin',
            entityLabel: entityLabel.trim() || null,
            note: journalNote.trim() || null,
          },
          organizationId: activeOrgId,
        },
        accessToken,
      );

      if (!result.success) {
        throw new Error(result.error || 'Could not save enterprise check-in');
      }

      appendEnterpriseWorkspaceCheckIn({
        id: result.checkIn?.id || `local-${Date.now()}`,
        organizationId: activeOrgId,
        relationshipDomain: domain,
        entityLabel: entityLabel.trim() || null,
        rtiScore,
        emotionalAverage,
        triggers,
        note: journalNote.trim() || null,
        occurredAt: result.checkIn?.occurred_at || new Date().toISOString(),
      });

      await refreshMicroActions(rtiScore, components, { accessToken, source: 'daily' });
      toast.success('Enterprise check-in saved and routed to org analytics.');
      navigate(workspacePath(location.pathname, 'home'));
    } catch (error: any) {
      toast.error(error?.message || 'Failed to submit enterprise check-in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen tension-page-bg">
      <EnterpriseWorkspaceNav />
      <div className="mx-auto max-w-2xl px-6 py-8">
        <div className="mb-4 rounded-xl border border-slate-700 bg-slate-900/80 px-4 py-3 text-sm text-slate-200">
          <p className="inline-flex items-center gap-2 font-semibold text-slate-100">
            <Building2 className="h-4 w-4 text-blue-300" aria-hidden />
            Enterprise org context
          </p>
          <p className="mt-1">{orgBadge}</p>
          {!hasActiveOrgAccess ? (
            <p className="mt-2 text-amber-300">You must be signed in with an active enterprise org membership.</p>
          ) : null}
        </div>

        <div className="mb-6 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate(workspacePath(location.pathname, 'home'))}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to workspace
          </Button>
        </div>

        <AnimatePresence mode="wait">
          {step === 'rti' && (
            <motion.div key="rti" {...fadeInUp} className="space-y-6">
              <Card className="p-6">
                <h1 className="mb-2 flex items-center gap-2 text-2xl font-bold text-slate-100">
                  <HeartPulse className="h-6 w-6 text-blue-300" aria-hidden />
                  Enterprise check-in
                </h1>
                <p className="mb-6 text-slate-300">Capture workforce-relevant relationship strain dynamics across all domains.</p>
                <p className="mb-5 rounded-lg border border-slate-700 bg-slate-900/70 px-3 py-2 text-sm text-slate-300">
                  Role guidance ({role}): {roleHint}
                </p>

                <div className="mb-5">
                  <Label className="mb-2 block text-slate-200">Relationship domain</Label>
                  <select
                    value={domain}
                    onChange={(e) => setDomain(e.target.value as RelationshipDomain)}
                    className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100"
                  >
                    <option value="leadership">Leadership / manager dynamics</option>
                    <option value="friendship">Peer / friendship dynamics</option>
                    <option value="family">Family dynamics affecting work</option>
                    <option value="romantic">Romantic dynamics affecting work</option>
                  </select>
                </div>

                <div className="mb-5">
                  <Label className="mb-2 block text-slate-200">Entity label (optional)</Label>
                  <input
                    value={entityLabel}
                    onChange={(e) => setEntityLabel(e.target.value)}
                    placeholder="Team Alpha manager cohort"
                    className="w-full rounded-lg border border-slate-600 bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-400"
                  />
                </div>

                <div className="space-y-6">
                  {[
                    ['Initiation balance', initiation, setInitiation, 'Who is initiating connection and repair attempts?'],
                    ['Response latency', latency, setLatency, 'How delayed are responses in important interactions?'],
                    ['Emotional depth', emotionalDensity, setEmotionalDensity, 'How emotionally safe and substantive are exchanges?'],
                    ['Availability', availability, setAvailability, 'How available and supportive is the relationship context?'],
                    ['Withdrawal patterns', withdrawal, setWithdrawal, 'How much shutdown/avoidance is currently present?'],
                  ].map(([label, value, setter, hint]) => (
                    <div key={label as string}>
                      <div className="mb-2 flex items-center justify-between">
                        <Label className="text-slate-200">{label as string}</Label>
                        <InfoHint title={label as string} description={hint as string} />
                      </div>
                      <Slider
                        value={value as number[]}
                        onValueChange={setter as (v: number[]) => void}
                        max={100}
                        min={0}
                        step={1}
                        className="w-full"
                      />
                      <div className="mt-1 text-right text-sm text-slate-300">{(value as number[])[0]}</div>
                    </div>
                  ))}
                </div>

                <Button className="mt-6 w-full" onClick={() => setStep('emotional')}>
                  Continue to emotional states
                </Button>
              </Card>
            </motion.div>
          )}

          {step === 'emotional' && (
            <motion.div key="emotional" {...slideInRight} className="space-y-6">
              <Card className="p-6">
                <h2 className="mb-4 text-xl font-semibold text-slate-100">Emotional state mapping</h2>
                <p className="mb-4 text-sm text-slate-300">Select up to five emotional states that best reflect current dynamics.</p>
                <EmotionalStateGauge
                  selectedStates={emotionalStates}
                  onStateSelect={handleEmotionalStateSelect}
                  maxSelections={5}
                />
                <div className="mt-6 flex gap-3">
                  <Button variant="outline" onClick={() => setStep('rti')}>Back</Button>
                  <Button onClick={() => setStep('triggers')} disabled={emotionalStates.length === 0}>Continue</Button>
                </div>
              </Card>
            </motion.div>
          )}

          {step === 'triggers' && (
            <motion.div key="triggers" {...slideInRight} className="space-y-6">
              <Card className="p-6">
                <h2 className="mb-4 text-xl font-semibold text-slate-100">Current triggers</h2>
                <TriggerSelector selectedTriggers={triggers} onTriggersChange={setTriggers} />
                <div className="mt-6 flex gap-3">
                  <Button variant="outline" onClick={() => setStep('emotional')}>Back</Button>
                  <Button onClick={() => (subscription === 'free' ? handleSubmit() : setStep('journal'))}>
                    {subscription === 'free' ? 'Submit check-in' : 'Continue'}
                  </Button>
                </div>
              </Card>
            </motion.div>
          )}

          {step === 'journal' && (
            <motion.div key="journal" {...slideInRight} className="space-y-6">
              <Card className="p-6">
                <h2 className="mb-4 text-xl font-semibold text-slate-100">Context note</h2>
                <ContextJournal
                  relationshipId={activeOrgId || 'enterprise-work'}
                  tier={subscription}
                  onSave={(note) => setJournalNote(note)}
                />
                <div className="mt-6">
                  <RTIExplanation component="withdrawal" score={withdrawal[0]} tier={subscription} />
                </div>
                <div className="mt-6 flex gap-3">
                  <Button variant="outline" onClick={() => setStep('triggers')}>Back</Button>
                  <Button onClick={handleSubmit} disabled={loading}>{loading ? 'Submitting...' : 'Submit enterprise check-in'}</Button>
                </div>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default withErrorBoundary(EnterpriseCheckIn, 'page');

